import { useMemo } from 'react';
import { useCompanyStock } from './useCompanyStock';
import { useInfiniteCompanyScreenersQuery } from '@/hooks/useInfiniteCompanyScreenersQuery/useInfiniteCompanyScreenersQuery';
import { filters } from '@/pages/Screener/constants';
import { getRandomItems } from '../utils';

const MARKET_CAP_TO_MILLIONS = 1_000_000;

/**
 * Returns market cap range multipliers based on company's current market cap
 * Goal:To increase the number of competitors for larger companies on smaller markets and industries
 * TODO: Monitor and adjust multipliers as needed
 * @param currentMarketCap - Current market cap in millions
 * @returns Object with min and max multipliers
 */
const getMarketCapRangeMultipliers = (currentMarketCap: number) => {
  if (currentMarketCap <= 500) {
    // Under 500 million: ±20%
    return { min: 0.8, max: 1.2 };
  } else if (currentMarketCap <= 1000) {
    // 501 million to 1 billion: ±30%
    return { min: 0.7, max: 1.3 };
  } else if (currentMarketCap <= 500000) {
    // 1 billion to 500 billion: ±40%
    return { min: 0.6, max: 1.4 };
  } else {
    // Over 500 billion: ±50%
    return { min: 0.5, max: 1.5 };
  }
};

export const useCompetitors = () => {
  const {
    companyId,
    uniqueSymbol,
    primaryIndustry,
    listingCurrencyISO,
    exchangeCountryISO,
    rawMarketCap,
  } = useCompanyStock();

  // Get the current company's market cap in millions
  const currentMarketCap = rawMarketCap
    ? rawMarketCap / MARKET_CAP_TO_MILLIONS
    : 0;

  // Get market cap range multipliers based on current market cap
  const { min: marketCapMinMultiplier, max: marketCapMaxMultiplier } =
    getMarketCapRangeMultipliers(currentMarketCap);

  // Calculate market cap range
  const marketCapMin = currentMarketCap * marketCapMinMultiplier;
  const marketCapMax = currentMarketCap * marketCapMaxMultiplier;

  // Create filters for the screener query
  const screenerRules = useMemo(() => {
    const rules = [
      // Market filter - same market as current company
      ['aor', [['country_name', 'in', [exchangeCountryISO?.toLowerCase()]]]],
      // Industry filter - same industry as current company
      ...(primaryIndustry.id
        ? [['aor', [['primary_industry_id', 'in', [primaryIndustry.id]]]]]
        : []),
      // Market cap range filter - only apply if we have a valid market cap
      ...(currentMarketCap > 0
        ? [[filters.marketcap, 'between', marketCapMin, marketCapMax]]
        : []),
      // Basic filters
      ['primary_flag', '=', true],
      ['grid_visible_flag', '=', true],
      ['is_fund', '=', false],
      ['market_cap', 'is_not_null'],
      // Order by market cap
      ['order_by', 'market_cap', 'desc'],
    ];

    return rules;
  }, [
    exchangeCountryISO,
    primaryIndustry.id,
    currentMarketCap,
    marketCapMin,
    marketCapMax,
  ]);

  // Query companies using the screener
  const { data: screenerData, status: screenerStatus } =
    useInfiniteCompanyScreenersQuery({
      rules: screenerRules,
      limit: 50,
      initPage: 0,
      enabled: Boolean(exchangeCountryISO),
    });

  // Extract all companies from all pages
  const allCompanies = useMemo(() => {
    if (!screenerData?.pages) return [];

    return screenerData.pages.flatMap(
      (page) =>
        page.companies?.map((company) => ({
          companyId: company.id,
          shortName: company.name,
          uniqueSymbol: company.uniqueSymbol,
          tickerSymbol: company.tickerSymbol,
          marketCap: company.analysisValue?.marketCap,
          website: company.info?.url,
          hasLogo: company.info?.hasLogo,
        })) || []
    );
  }, [screenerData]);

  // Filter out the current company and select 8 random companies
  const filteredCompanies = allCompanies.filter(
    (c) => c.companyId && c.companyId.toLowerCase() !== companyId.toLowerCase()
  );

  const randomCompanies = getRandomItems(filteredCompanies, 8, companyId);

  return {
    companyId,
    uniqueSymbol,
    listingCurrencyISO,
    primaryIndustry,
    exchangeCountryISO,
    competitors: randomCompanies,
    status: screenerStatus,
  };
};
